import { useOrganization } from '@clerk/clerk-react';
import { observer, useSelector } from '@legendapp/state/react';
import { useQuery } from '@tanstack/react-query';
import { BackendNoAuthClient, type BackendTypes, CSClient, TDMClient } from '@tf/api';
import { TFErrorTemplate, useGetAuthToken } from '@tf/shared';
import { TFLoadingOverlay } from '@tf/ui';
import type React from 'react';
import { useEffect } from 'react';

import { useTenantCapabilities } from '@/core/api/users';
import { getDefinitionsQuery, getProcessingScriptsQuery, getTenantConfigQuery } from '@/core/queries';
import { appStore, configStore } from '@/core/stores';

import { BackendClientInitialization } from './BackendClientInitialization';
import { PublicApiClientInitialization } from './PublicApiClientInitialization';
import { applyVisualModifierPatches } from './visualModifiers';

const useInitialDataQuery = () => {
	const config = useSelector(() => configStore.get());
	const getAuthToken = useGetAuthToken();
	const { organization } = useOrganization();

	return useQuery({
		queryKey: ['initialData'],
		enabled: false,
		queryFn: async () => {
			TDMClient.init(config.urls.tdm);
			CSClient.init(config.urls.cs);
			BackendNoAuthClient.init(config.urls.backend);

			const token = await getAuthToken();
			const tenant = organization?.slug!;

			const tenantConfig = await getTenantConfigQuery(tenant);

			const [appMeta, defs, processingScripts] = await Promise.all([
				BackendNoAuthClient.getInstance()
					.appMeta({
						headers: {
							Authorization: `Bearer ${token}`,
							'Configuration-Revision': tenantConfig.revision,
						},
					})
					.then((res: any) => res.json()) as Promise<BackendTypes.AppMetaData>,
				getDefinitionsQuery(tenantConfig.revision, tenantConfig.default_locale),
				getProcessingScriptsQuery(tenantConfig.revision),
			]);

			const modifiedDefs = applyVisualModifierPatches(defs);

			return {
				tenant: tenant,
				currency: tenantConfig.currency,
				defaultLocale: tenantConfig.default_locale,
				appMeta,
				defs: modifiedDefs,
				defsRevision: tenantConfig.revision,
				processingScripts,
			};
		},
	});
};

// TODO: use context instead of store
export const StateProvider: React.FC<React.PropsWithChildren> = observer(function StateProvider({ children }) {
	const initialDataQuery = useInitialDataQuery();
	const { isLoading: isTenantCapabilitiesLoading } = useTenantCapabilities();

	useEffect(() => {
		initialDataQuery.refetch().then((result) => {
			if (!result.data) {
				return;
			}

			const { tenant, appMeta, defs, processingScripts, currency, defaultLocale } = result.data;

			appStore.set((prev) => ({
				...prev,
				appMeta,
				tenant,
				currency,
				defaultLocale,
				defs,
				processingScripts,
			}));
		});
		// Extra deps cause more refetches
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialDataQuery.refetch]);

	if (initialDataQuery.status === 'error') {
		return <TFErrorTemplate />;
	}

	if (initialDataQuery.isPending || isTenantCapabilitiesLoading) {
		return <TFLoadingOverlay />;
	}

	return (
		<>
			<PublicApiClientInitialization />
			<BackendClientInitialization definitionsRevision={initialDataQuery.data?.defsRevision ?? ''} />
			{children}
		</>
	);
});
